/**
 * QuoteAcceptPage — Öffentliche KV-Seite (Annehmen ODER Ablehnen).
 *
 * Aufruf: /quote-accept/:token  (kein Auth – Token-basiert)
 * Ablauf:
 *   1. GET /api/work-orders/quote-view/{token} → KV-Zusammenfassung + PDF-Link
 *   2. „Annehmen"  → POST /api/work-orders/quote-accept/{token}
 *   3. „Ablehnen"  → Bestätigung (10%-Gebühr-Hinweis) → POST .../quote-reject/{token}
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    CheckCircle2, AlertTriangle, Wrench, Loader2, FileText, XCircle, Bike,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => Number(n || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function QuoteAcceptPage() {
    const { token } = useParams();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmReject, setConfirmReject] = useState(false);
    const [result, setResult] = useState(null); // {type: 'accepted'|'rejected', ...}

    useEffect(() => {
        if (!token) { setError("Kein Token angegeben."); setLoading(false); return; }
        axios.get(`${API}/work-orders/quote-view/${token}`)
            .then((r) => setQuote(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Link ungültig oder abgelaufen."))
            .finally(() => setLoading(false));
    }, [token]);

    const accept = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/work-orders/quote-accept/${token}`);
            setResult({ type: "accepted", ...r.data });
        } catch (e) { setError(e.response?.data?.detail || "Annahme fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const reject = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/work-orders/quote-reject/${token}`);
            setResult({ type: "rejected", ...r.data });
        } catch (e) { setError(e.response?.data?.detail || "Ablehnung fehlgeschlagen."); }
        finally { setBusy(false); setConfirmReject(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8" data-testid="quote-accept-page">
            <div className="bg-white border border-gray-200 rounded-sm w-full max-w-lg p-8">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-3">
                    <Wrench className="w-4 h-4 text-[#111827]" />
                    Kostenvoranschlag · TwoWheels.de
                </div>

                {loading && (
                    <div className="text-center py-8" data-testid="quote-accept-loading">
                        <Loader2 className="w-10 h-10 mx-auto animate-spin text-gray-400" />
                        <div className="mt-3 text-sm text-gray-600">Kostenvoranschlag wird geladen…</div>
                    </div>
                )}

                {!loading && error && !result && (
                    <div className="text-center py-4" data-testid="quote-accept-error">
                        <AlertTriangle className="w-16 h-16 mx-auto text-red-600" />
                        <h1 className="font-display font-black text-2xl mt-3">Das hat nicht geklappt</h1>
                        <p className="text-sm text-gray-600 mt-2">{error}</p>
                        <p className="text-xs text-gray-500 mt-3">Bitte wenden Sie sich direkt an die Werkstatt.</p>
                    </div>
                )}

                {!loading && result?.type === "accepted" && (
                    <div className="text-center py-4" data-testid="quote-accept-success">
                        <CheckCircle2 className="w-16 h-16 mx-auto text-green-600" />
                        <h1 className="font-display font-black text-2xl mt-3">{result.already_accepted ? "Schon angenommen" : "Auftrag angenommen!"}</h1>
                        <p className="text-sm text-gray-600 mt-2">
                            {result.already_accepted
                                ? "Dieser Kostenvoranschlag wurde bereits angenommen. Bei Fragen wenden Sie sich gerne direkt an die Werkstatt."
                                : "Vielen Dank. Wir haben Ihre Auftragsbestätigung erhalten und melden uns kurzfristig mit einem Termin-Vorschlag."}
                        </p>
                        {result.order_number && (
                            <div className="mt-4 inline-block bg-gray-100 px-3 py-1.5 rounded-sm font-mono-num text-sm font-bold" data-testid="quote-accept-order-number">
                                {result.order_number}
                            </div>
                        )}
                    </div>
                )}

                {!loading && result?.type === "rejected" && (
                    <div className="text-center py-4" data-testid="quote-reject-success">
                        <XCircle className="w-16 h-16 mx-auto text-gray-500" />
                        <h1 className="font-display font-black text-2xl mt-3">Kostenvoranschlag abgelehnt</h1>
                        <p className="text-sm text-gray-600 mt-2">
                            Ihre Ablehnung wurde dokumentiert. Gemäß den Hinweisen im Kostenvoranschlag fällt eine Gebühr von{" "}
                            <b>10 % der Kostenvoranschlagssumme{result.rejection_fee != null ? ` (${eur(result.rejection_fee)})` : ""}</b>{" "}
                            an, die bei Abholung des Fahrzeugs fällig ist.
                        </p>
                        {result.order_number && (
                            <div className="mt-4 inline-block bg-gray-100 px-3 py-1.5 rounded-sm font-mono-num text-sm font-bold">
                                {result.order_number}
                            </div>
                        )}
                    </div>
                )}

                {!loading && !error && !result && quote && (
                    <div data-testid="quote-view">
                        <h1 className="font-display font-black text-2xl">Ihr Kostenvoranschlag</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Guten Tag {quote.customer_name}, hier ist der Kostenvoranschlag von <b>{quote.dealer_name}</b>{quote.dealer_city ? ` (${quote.dealer_city})` : ""}.
                        </p>

                        <div className="mt-4 border border-gray-200 rounded-sm divide-y divide-gray-100">
                            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
                                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Auftrag</span>
                                <span className="font-mono-num text-sm font-bold" data-testid="quote-view-order-number">{quote.order_number || "—"}</span>
                            </div>
                            {quote.vehicle_label && (
                                <div className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700">
                                    <Bike className="w-4 h-4 text-gray-400" /> {quote.vehicle_label}
                                </div>
                            )}
                            {(quote.positions || []).map((p, i) => (
                                <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                                    <span className="text-gray-700 min-w-0 truncate pr-3">{p.description}{p.quantity ? ` · ${p.quantity} ${p.unit || ""}`.trimEnd() : ""}</span>
                                    <span className="font-bold text-[#111827] whitespace-nowrap">{eur(p.line_gross)}</span>
                                </div>
                            ))}
                            <div className="flex items-center justify-between px-4 py-3 bg-[#FFF7CC]">
                                <span className="font-black text-[#111827]">Gesamt (inkl. USt.)</span>
                                <span className="font-black text-[#111827] text-lg" data-testid="quote-view-total">{eur(quote.total_gross)}</span>
                            </div>
                        </div>

                        {quote.valid_until && (
                            <p className="text-xs text-gray-500 mt-2">Unverbindlich · gültig bis {quote.valid_until}.</p>
                        )}
                        <a href={`${API}/work-orders/quote-view/${token}/pdf`} target="_blank" rel="noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#111827] underline decoration-[#FFCC00] decoration-2"
                            data-testid="quote-view-pdf-link">
                            <FileText className="w-4 h-4" /> Kostenvoranschlag als PDF ansehen
                        </a>

                        {quote.decision === "accepted" && (
                            <div className="mt-5 flex items-start gap-2 rounded-sm border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" data-testid="quote-view-already-accepted">
                                <CheckCircle2 className="w-5 h-5 shrink-0" /> Dieser Kostenvoranschlag wurde bereits angenommen.
                            </div>
                        )}
                        {quote.decision === "rejected" && (
                            <div className="mt-5 flex items-start gap-2 rounded-sm border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700" data-testid="quote-view-already-rejected">
                                <XCircle className="w-5 h-5 shrink-0" /> Dieser Kostenvoranschlag wurde bereits abgelehnt{quote.rejection_fee != null ? ` — dokumentierte Gebühr ${eur(quote.rejection_fee)} (fällig bei Abholung)` : ""}.
                            </div>
                        )}
                        {quote.decision === "closed" && (
                            <div className="mt-5 flex items-start gap-2 rounded-sm border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                                <AlertTriangle className="w-5 h-5 shrink-0" /> Dieser Auftrag ist bereits geschlossen. Bitte wenden Sie sich an die Werkstatt.
                            </div>
                        )}

                        {quote.decision === "open" && !confirmReject && (
                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button onClick={accept} disabled={busy}
                                    className="inline-flex items-center justify-center gap-2 bg-green-600 text-white rounded-sm px-4 py-3 text-sm font-black hover:bg-green-700 disabled:opacity-60"
                                    data-testid="quote-view-accept-btn">
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Annehmen
                                </button>
                                <button onClick={() => setConfirmReject(true)} disabled={busy}
                                    className="inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 rounded-sm px-4 py-3 text-sm font-black hover:bg-gray-50 disabled:opacity-60"
                                    data-testid="quote-view-reject-btn">
                                    <XCircle className="w-4 h-4" /> Ablehnen
                                </button>
                            </div>
                        )}

                        {quote.decision === "open" && confirmReject && (
                            <div className="mt-6 rounded-sm border border-amber-300 bg-amber-50 p-4" data-testid="quote-reject-confirm">
                                <div className="flex items-start gap-2 text-sm text-amber-900">
                                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                                    <p>
                                        <b>Wichtiger Hinweis:</b> Bei Nichtannahme des Kostenvoranschlags entsteht eine Gebühr von{" "}
                                        <b>{Number(quote.fee_pct || 10)} % der Kostenvoranschlagssumme ({eur(quote.fee_if_rejected)})</b>,
                                        die bei Abholung des Fahrzeugs fällig ist. Möchten Sie wirklich ablehnen?
                                    </p>
                                </div>
                                <div className="mt-3 flex gap-2 justify-end">
                                    <button onClick={() => setConfirmReject(false)} disabled={busy}
                                        className="rounded-sm border border-gray-300 bg-white px-4 py-2 text-sm font-bold hover:bg-gray-50"
                                        data-testid="quote-reject-cancel-btn">Zurück</button>
                                    <button onClick={reject} disabled={busy}
                                        className="inline-flex items-center gap-2 bg-red-600 text-white rounded-sm px-4 py-2 text-sm font-black hover:bg-red-700 disabled:opacity-60"
                                        data-testid="quote-reject-confirm-btn">
                                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Verbindlich ablehnen
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
